import type { CSSProperties } from 'react';

export type TooltipPlacement = 'top' | 'bottom';

export interface GeometryInput {
  anchorRect: Pick<DOMRect, 'top' | 'bottom' | 'left' | 'right' | 'width' | 'height'>;
  maxWidth?: number;
  minWidth?: number;
  popoverRect: Pick<DOMRect, 'width' | 'height'>;
  preferred?: TooltipPlacement;
  scrollX: number;
  scrollY: number;
  space: number;
  viewportHeight: number;
  viewportWidth: number;
  width?: number;
}

export interface GeometryResult {
  placement: TooltipPlacement;
  tooltip: CSSProperties;
  tooltipArrow: { [key: string]: string };
}

export const choosePlacement = (input: GeometryInput): TooltipPlacement => {
  const { anchorRect, popoverRect, preferred, space, viewportHeight } = input;
  const hasRoomOnTop = anchorRect.top > popoverRect.height + space;
  const hasRoomOnBottom = viewportHeight - anchorRect.bottom > popoverRect.height + space;

  if (preferred === 'bottom' && hasRoomOnBottom) return 'bottom';
  if (preferred === 'bottom' && !hasRoomOnBottom && hasRoomOnTop) return 'top';
  if (preferred === 'top' && hasRoomOnTop) return 'top';
  if (preferred === 'top' && !hasRoomOnTop && hasRoomOnBottom) return 'bottom';
  if (hasRoomOnTop) return 'top';
  if (hasRoomOnBottom) return 'bottom';
  return preferred ?? 'top';
};

export const computeTooltipGeometry = (input: GeometryInput): GeometryResult => {
  const {
    anchorRect,
    maxWidth,
    minWidth,
    popoverRect,
    preferred,
    scrollX,
    scrollY,
    space,
    viewportHeight,
    viewportWidth,
    width,
  } = input;
  const naturalWidth = width ?? popoverRect.width;
  const boundedMin = typeof minWidth === 'number' ? minWidth : 0;
  const boundedMax = typeof maxWidth === 'number' ? maxWidth : naturalWidth;
  const tooltipWidth = Math.max(boundedMin, Math.min(naturalWidth, boundedMax));
  const anchorCenterX = (anchorRect.left + anchorRect.right) / 2;
  const preferredLeft = anchorCenterX - tooltipWidth / 2;
  const minLeft = space;
  const maxLeft = viewportWidth - tooltipWidth - space;
  const clampedLeft = Math.max(minLeft, Math.min(preferredLeft, maxLeft));

  const placement = choosePlacement({
    anchorRect,
    popoverRect,
    preferred,
    scrollX,
    scrollY,
    space,
    viewportHeight,
    viewportWidth,
    width: tooltipWidth,
  });

  const topForTopPlacement = anchorRect.top + scrollY - popoverRect.height - space;
  const topForBottomPlacement = anchorRect.bottom + scrollY + space;
  const tooltipTop = placement === 'top' ? topForTopPlacement : topForBottomPlacement;

  const rawArrowOffset = anchorCenterX - clampedLeft;
  const arrowOffset = Math.max(8, Math.min(tooltipWidth - 8, rawArrowOffset));
  const tooltipArrow: { [key: string]: string } = {
    left: `${arrowOffset}px`,
  };

  if (placement === 'bottom') {
    tooltipArrow.top = '-10px';
    tooltipArrow.transform = 'rotateX(180deg)';
  } else {
    tooltipArrow.top = '100%';
  }

  const tooltip: CSSProperties = {
    left: clampedLeft + scrollX,
    top: tooltipTop,
    width: tooltipWidth,
  };

  return { placement, tooltip, tooltipArrow };
};
